import { useEffect, useRef } from 'react'
import { safeStorage } from '@/lib/storage'

/**
 * Sign a tenant out after fifteen minutes without activity, from the browser's side.
 *
 * The server already refuses a session idle past `IDLE_TIMEOUT_SECONDS`
 * (`api/app/security/sessions.py`), but it can only refuse when asked. A tab
 * left open on a dashboard would sit there, apparently signed in, until the
 * next click — and then meet a 401. Worse, anything that polls would keep the
 * server-side clock sliding for a person who is not there. So the same number
 * is enforced here: no *human* activity for fifteen minutes and the app signs
 * itself out, whatever the network has been doing.
 *
 * **Activity is what a person does**, not what the page does. Pointer, key,
 * wheel, touch and scroll — never a fetch, never a re-render, so a background
 * refetch cannot keep a session alive.
 *
 * **Shared across tabs.** The latest activity is written to `localStorage` so
 * two tabs on the same site agree: typing in one is being present in the
 * other. Without that a second tab, opened and forgotten, would sign the first
 * one out from under the person using it — every tab shares the cookie, so a
 * sign-out anywhere is a sign-out everywhere. The write is throttled to once
 * every few seconds; the deadline is checked coarsely and again whenever the
 * tab becomes visible, because a background tab's timers are throttled by the
 * browser and a laptop lid closed for an hour fires nothing at all.
 */

/** Mirrors `IDLE_TIMEOUT_SECONDS` in `api/app/security/sessions.py` (15 * 60). */
export const IDLE_TIMEOUT_MINUTES = 15
export const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60_000

/** How often the deadline is checked. Coarse on purpose — nobody needs to be signed out to the second. */
const CHECK_EVERY_MS = 10_000
/** How often this tab tells the others it is in use. */
const SHARE_EVERY_MS = 5_000
const ACTIVITY_KEY = 'augrank.last-activity'

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const

/**
 * @param enabled  Only while somebody is signed in. Off, the hook does nothing at all.
 * @param onIdle   Called once per idle period; re-arms when `enabled` goes false and true again.
 */
export function useIdleSignOut(
  enabled: boolean,
  onIdle: () => void,
  timeoutMs: number = IDLE_TIMEOUT_MS,
): void {
  // The latest callback, so a stale closure can never sign out with an old
  // identity's handler — and so changing it does not restart the clock.
  const latest = useRef(onIdle)
  latest.current = onIdle

  useEffect(() => {
    if (!enabled) return

    const storage = safeStorage()
    let lastActivity = Date.now()
    let lastShared = 0
    let fired = false

    const share = (): void => {
      lastShared = lastActivity
      storage.setItem(ACTIVITY_KEY, String(lastActivity))
    }
    // Signing in is activity, and it is what other tabs must see: a fresh
    // session must not inherit a stale timestamp another tab left behind.
    share()

    const active = (): void => {
      lastActivity = Date.now()
      if (lastActivity - lastShared >= SHARE_EVERY_MS) share()
    }

    const check = (): void => {
      if (fired) return
      const shared = Number(storage.getItem(ACTIVITY_KEY))
      const last = Number.isFinite(shared) ? Math.max(lastActivity, shared) : lastActivity
      if (Date.now() - last < timeoutMs) return
      fired = true
      latest.current()
    }

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') check()
    }

    for (const name of ACTIVITY_EVENTS) {
      window.addEventListener(name, active, { passive: true, capture: true })
    }
    document.addEventListener('visibilitychange', onVisibility)
    const timer = window.setInterval(check, CHECK_EVERY_MS)

    return () => {
      for (const name of ACTIVITY_EVENTS) {
        window.removeEventListener(name, active, { capture: true })
      }
      document.removeEventListener('visibilitychange', onVisibility)
      window.clearInterval(timer)
    }
  }, [enabled, timeoutMs])
}
